import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Cameraswitch, CheckCircleRounded } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { FaceMesh, FACEMESH_TESSELATION } from '@mediapipe/face_mesh';
import FaceGuideOverlay from './FaceGuideOverlay';
import {
  playEnrollmentVoice,
  playLightingLowVoice,
  resetEnrollmentVoice,
  shouldPlayVoiceForGuidance,
} from '../../utils/faceEnrollment/EnrollmentVoiceGuide';
import { analyzeLightingFromVideo, mergeEnrollmentValidation } from '../../utils/faceEnrollment/LightingValidationService';
import { getDefaultStepPrompt, validateFaceForStep } from '../../utils/faceEnrollment/FaceValidationService';
import {
  buildVideoConstraints,
  enumerateVideoInputDevices,
  findOppositeFacingDevice,
  getNextDeviceId,
  getOppositeFacingMode,
  isMobileUserAgent,
  pickDefaultVideoDeviceId,
  readStreamFacingMode,
  resolveVideoDeviceId,
  shouldMirrorVideo,
} from '../../utils/mediaCameras';

const DETECTION_INTERVAL_MS = 120;
const LIGHTING_INTERVAL_MS = 200;
const COUNTDOWN_SECONDS = 3;
const CAPTURE_EFFECT_MS = 420;
const CAPTURE_CALLBACK_DELAY_MS = 110;
const GUIDANCE_STABLE_FRAME_COUNT = 4;
const GUIDANCE_CHANGE_MIN_INTERVAL_MS = 1000;
const MESH_CONNECTIONS = Array.from(FACEMESH_TESSELATION || []);
const PASSPORT_ASPECT_RATIO = 35 / 45; // 35x45mm portrait ratio
const HEAD_RATIO_TARGET = 0.74; // Chin-to-crown should occupy ~70-80% of frame
const MIN_TOP_MARGIN_RATIO = 0.16; // keep space above head for hair/forehead
const SIDE_MARGIN_FROM_FACE_RATIO = 0.18;
const FACE_CROP_MIN_SIZE = 140;
const CAPTURE_SOUND_SOURCE = '/face_enrolment_voices/camera-capture.mp3';

// MediaPipe Face Mesh uses one WASM module per page. Closing it and creating
// another (register, then edit) leaves the next instance unable to detect faces.
let sharedFaceMesh = null;
let sharedFaceMeshInit = null;

const getSharedFaceMesh = () => {
  if (sharedFaceMesh) return Promise.resolve(sharedFaceMesh);
  if (!sharedFaceMeshInit) {
    sharedFaceMeshInit = (async () => {
      const faceMesh = new FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });
      faceMesh.setOptions({
        maxNumFaces: 2,
        refineLandmarks: true,
        minDetectionConfidence: 0.6,
        minTrackingConfidence: 0.6,
      });
      sharedFaceMesh = faceMesh;
      return faceMesh;
    })().catch((error) => {
      sharedFaceMeshInit = null;
      throw error;
    });
  }
  return sharedFaceMeshInit;
};

const clearTimer = (timerRef) => {
  if (timerRef.current) {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pickLandmark = (landmarks, primaryIndex, fallbackIndex = null) => {
  if (!Array.isArray(landmarks)) return null;
  if (landmarks[primaryIndex]) return landmarks[primaryIndex];
  if (fallbackIndex !== null && landmarks[fallbackIndex]) return landmarks[fallbackIndex];
  return null;
};

const WebcamCapture = ({
  stepKey,
  expectedDirection,
  onCaptured,
  onPermissionDenied,
  preferredDeviceId = '',
  onPreferredDeviceChange,
  onCameraReady,
}) => {
  const webcamRef = useRef(null);
  const cameraViewportRef = useRef(null);
  const meshCanvasRef = useRef(null);
  const lightingCanvasRef = useRef(null);
  const frameRequestRef = useRef(null);
  const isSendingRef = useRef(false);
  const lastFrameAtRef = useRef(0);
  const lastLightingAtRef = useRef(0);
  const latestLightingRef = useRef(null);
  const latestValidationRef = useRef(null);
  const latestLandmarksRef = useRef(null);
  const lastVoiceGuidanceRef = useRef('');
  const faceMeshRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const captureEffectTimerRef = useRef(null);
  const captureCallbackTimerRef = useRef(null);
  const hasCapturedRef = useRef(false);
  const mountedRef = useRef(true);
  const expectedDirectionRef = useRef(expectedDirection);
  const onCapturedRef = useRef(onCaptured);
  const captureSoundRef = useRef(null);
  const stableGuidanceRef = useRef(getDefaultStepPrompt(expectedDirection));
  const guidanceCandidateRef = useRef({ value: '', count: 0 });
  const lastGuidanceSwitchAtRef = useRef(0);

  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileDevice = useMemo(() => isMobileUserAgent(), []);

  const [modelReady, setModelReady] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [modelError, setModelError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [guidance, setGuidance] = useState(getDefaultStepPrompt(expectedDirection));
  const [countdown, setCountdown] = useState(0);
  const [faceBounds, setFaceBounds] = useState(null);
  const [captureFxActive, setCaptureFxActive] = useState(false);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(preferredDeviceId || '');
  const [activeFacingMode, setActiveFacingMode] = useState('user');
  const [cameraSessionKey, setCameraSessionKey] = useState(0);
  const selectedDeviceIdRef = useRef(preferredDeviceId || '');
  const preferredDeviceIdRef = useRef(preferredDeviceId || '');
  const isCameraReadyRef = useRef(false);
  const isMirroredRef = useRef(true);

  const updateGuidanceSmoothly = useCallback((nextGuidance) => {
    const fallback = getDefaultStepPrompt(expectedDirectionRef.current);
    const targetGuidance = nextGuidance || fallback;
    const now = Date.now();

    if (targetGuidance === stableGuidanceRef.current) {
      guidanceCandidateRef.current = { value: targetGuidance, count: 0 };
      return stableGuidanceRef.current;
    }

    if (guidanceCandidateRef.current.value !== targetGuidance) {
      guidanceCandidateRef.current = { value: targetGuidance, count: 1 };
      return stableGuidanceRef.current;
    }

    guidanceCandidateRef.current.count += 1;
    const hasEnoughFrames = guidanceCandidateRef.current.count >= GUIDANCE_STABLE_FRAME_COUNT;
    const switchedRecently = now - lastGuidanceSwitchAtRef.current < GUIDANCE_CHANGE_MIN_INTERVAL_MS;

    if (!hasEnoughFrames || switchedRecently) {
      return stableGuidanceRef.current;
    }

    stableGuidanceRef.current = targetGuidance;
    lastGuidanceSwitchAtRef.current = now;
    guidanceCandidateRef.current = { value: targetGuidance, count: 0 };
    setGuidance(targetGuidance);

    if (lastVoiceGuidanceRef.current !== targetGuidance) {
      lastVoiceGuidanceRef.current = targetGuidance;
      const guidanceKey = targetGuidance.trim().toLowerCase();
      if (guidanceKey === 'lighting too low' || guidanceKey === 'too bright') {
        void playLightingLowVoice();
      } else if (shouldPlayVoiceForGuidance(targetGuidance)) {
        void playEnrollmentVoice(targetGuidance);
      }
    }

    return targetGuidance;
  }, []);

  const runLightingAnalysis = useCallback((faceBounds = null) => {
    const video = webcamRef.current?.video;
    const canvas = lightingCanvasRef.current;
    if (!video || !canvas || video.readyState < 2) return null;

    const lighting = analyzeLightingFromVideo(video, canvas, faceBounds);
    latestLightingRef.current = lighting;
    return lighting;
  }, []);

  const videoConstraints = useMemo(
    () =>
      buildVideoConstraints(selectedDeviceId, {
        mobile: isMobileDevice,
        facingMode: activeFacingMode,
      }),
    [activeFacingMode, isMobileDevice, selectedDeviceId]
  );

  const isMirrored = shouldMirrorVideo(activeFacingMode);
  isMirroredRef.current = isMirrored;

  const showMobileCameraSwitch = isMobileLayout || isMobileDevice;
  const showDesktopCameraSelect = !showMobileCameraSwitch && videoDevices.length > 1;

  const applySelectedDevice = useCallback((deviceId, { remount = false } = {}) => {
    if (!deviceId || deviceId === selectedDeviceIdRef.current) return;

    selectedDeviceIdRef.current = deviceId;
    setSelectedDeviceId(deviceId);
    if (remount) {
      setCameraSessionKey((previous) => previous + 1);
    }
  }, []);

  const refreshVideoDevices = useCallback(async () => {
    try {
      const devices = await enumerateVideoInputDevices();
      setVideoDevices(devices);

      if (!devices.length) return;

      // Keep the active stream after the user (or initial) selection — only refresh the device list.
      if (isCameraReadyRef.current) {
        return;
      }

      const nextId = resolveVideoDeviceId(devices, {
        currentId: selectedDeviceIdRef.current,
        preferredId: preferredDeviceIdRef.current,
      });

      if (!nextId) return;

      applySelectedDevice(nextId, { remount: !selectedDeviceIdRef.current });
    } catch (error) {
      console.error('Failed to enumerate cameras:', error);
    }
  }, [applySelectedDevice, isMobileDevice]);

  const stopStream = () => {
    const stream = webcamRef.current?.video?.srcObject;
    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (webcamRef.current?.video) {
      webcamRef.current.video.srcObject = null;
    }
  };

  const ensureCanvasSize = useCallback(() => {
    const canvas = meshCanvasRef.current;
    const video = webcamRef.current?.video;
    if (!canvas || !video) return;
    if (!video.videoWidth || !video.videoHeight) return;

    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
  }, []);

  const drawFaceMesh = useCallback((landmarks) => {
    const canvas = meshCanvasRef.current;
    const video = webcamRef.current?.video;
    if (!canvas || !video) return;

    ensureCanvasSize();

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);

    if (!landmarks || !landmarks.length) return;

    const mirrorX = isMirroredRef.current;
    const getX = (point) => (mirrorX ? (1 - point.x) : point.x) * width;
    const getY = (point) => point.y * height;

    ctx.strokeStyle = 'rgba(56, 189, 248, 0.72)';
    ctx.lineWidth = Math.max(0.9, width / 1400);
    ctx.beginPath();
    for (let i = 0; i < MESH_CONNECTIONS.length; i += 1) {
      const connection = MESH_CONNECTIONS[i];
      const startIndex = Array.isArray(connection) ? connection[0] : connection?.start;
      const endIndex = Array.isArray(connection) ? connection[1] : connection?.end;
      if (startIndex == null || endIndex == null) continue;
      const start = landmarks[startIndex];
      const end = landmarks[endIndex];
      if (!start || !end) continue;
      ctx.moveTo(getX(start), getY(start));
      ctx.lineTo(getX(end), getY(end));
    }
    ctx.stroke();

    ctx.fillStyle = 'rgba(59, 130, 246, 0.55)';
    const pointRadius = Math.max(0.75, width / 900);
    for (let i = 0; i < landmarks.length; i += 6) {
      const point = landmarks[i];
      if (!point) continue;
      ctx.beginPath();
      ctx.arc(getX(point), getY(point), pointRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [ensureCanvasSize]);

  useEffect(() => {
    selectedDeviceIdRef.current = selectedDeviceId;
  }, [selectedDeviceId]);

  useEffect(() => {
    preferredDeviceIdRef.current = preferredDeviceId || '';
  }, [preferredDeviceId]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) {
      return undefined;
    }

    const handleDeviceChange = () => {
      void refreshVideoDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [refreshVideoDevices]);

  useEffect(() => {
    if (isMobileDevice) return undefined;

    let cancelled = false;

    const primeDesktopDevice = async () => {
      try {
        const devices = await enumerateVideoInputDevices();
        if (cancelled || !devices.length) return;

        setVideoDevices(devices);

        if (selectedDeviceIdRef.current || preferredDeviceIdRef.current) return;

        const defaultId = pickDefaultVideoDeviceId(devices, 'user');
        if (!defaultId) return;

        selectedDeviceIdRef.current = defaultId;
        setSelectedDeviceId(defaultId);
      } catch (error) {
        console.error('Failed to prime desktop camera list:', error);
      }
    };

    void primeDesktopDevice();

    return () => {
      cancelled = true;
    };
  }, [isMobileDevice]);

  useEffect(() => {
    expectedDirectionRef.current = expectedDirection;
  }, [expectedDirection]);

  useEffect(() => {
    onCapturedRef.current = onCaptured;
  }, [onCaptured]);

  const resetForStep = useCallback(() => {
    clearTimer(countdownTimerRef);
    clearTimer(captureEffectTimerRef);
    clearTimer(captureCallbackTimerRef);
    hasCapturedRef.current = false;
    latestValidationRef.current = null;
    latestLandmarksRef.current = null;
    latestLightingRef.current = null;
    lastLightingAtRef.current = 0;
    lastVoiceGuidanceRef.current = '';
    setCountdown(0);
    setCaptureFxActive(false);
    setFaceBounds(null);
    const defaultGuidance = getDefaultStepPrompt(expectedDirectionRef.current);
    stableGuidanceRef.current = defaultGuidance;
    guidanceCandidateRef.current = { value: defaultGuidance, count: 0 };
    lastGuidanceSwitchAtRef.current = 0;
    setGuidance(defaultGuidance);
  }, []);

  const switchToFacingMode = useCallback(
    (facingMode) => {
      const nextFacing = facingMode === 'environment' ? 'environment' : 'user';
      if (nextFacing === activeFacingMode) return;

      stopStream();
      isCameraReadyRef.current = false;
      selectedDeviceIdRef.current = '';
      setSelectedDeviceId('');
      setActiveFacingMode(nextFacing);
      setIsCameraReady(false);
      setCameraError('');
      setCameraSessionKey((previous) => previous + 1);
      onPreferredDeviceChange?.('');
      resetForStep();
    },
    [activeFacingMode, onPreferredDeviceChange, resetForStep]
  );

  const switchToDevice = useCallback(
    (deviceId) => {
      if (!deviceId) return;

      const device = videoDevices.find((entry) => entry.deviceId === deviceId);
      stopStream();
      isCameraReadyRef.current = false;
      selectedDeviceIdRef.current = deviceId;
      setSelectedDeviceId(deviceId);
      setActiveFacingMode(device?.facingHint === 'environment' ? 'environment' : 'user');
      setCameraSessionKey((previous) => previous + 1);
      onPreferredDeviceChange?.(deviceId);
      setIsCameraReady(false);
      setCameraError('');
      resetForStep();
    },
    [onPreferredDeviceChange, resetForStep, videoDevices]
  );

  useEffect(() => {
    if (isMobileDevice) return;
    if (!preferredDeviceId || preferredDeviceId === selectedDeviceIdRef.current) return;
    if (!videoDevices.some((device) => device.deviceId === preferredDeviceId)) return;

    switchToDevice(preferredDeviceId);
  }, [isMobileDevice, preferredDeviceId, switchToDevice, videoDevices]);

  const handleFlipCamera = useCallback(() => {
    if (isMobileDevice) {
      switchToFacingMode(getOppositeFacingMode(activeFacingMode));
      return;
    }

    const opposite = findOppositeFacingDevice(videoDevices, selectedDeviceIdRef.current);
    if (opposite?.deviceId) {
      switchToDevice(opposite.deviceId);
      return;
    }
    const nextId = getNextDeviceId(videoDevices, selectedDeviceIdRef.current);
    if (nextId) switchToDevice(nextId);
  }, [activeFacingMode, isMobileDevice, switchToDevice, switchToFacingMode, videoDevices]);

  const handleCameraSelectChange = useCallback(
    (event) => {
      switchToDevice(event.target.value);
    },
    [switchToDevice]
  );

  const captureFaceOnlyImage = useCallback(() => {
    const video = webcamRef.current?.video;
    const bounds = latestValidationRef.current?.bounds;
    const landmarks = latestLandmarksRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      return webcamRef.current?.getScreenshot() || null;
    }

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    const leftCheek = pickLandmark(landmarks, 234);
    const rightCheek = pickLandmark(landmarks, 454);
    const foreheadTop = pickLandmark(landmarks, 10, 151);
    const chinBottom = pickLandmark(landmarks, 152);

    let faceMinX;
    let faceMaxX;
    let faceTopY;
    let faceBottomY;

    if (leftCheek && rightCheek && foreheadTop && chinBottom) {
      faceMinX = Math.min(leftCheek.x, rightCheek.x) * videoWidth;
      faceMaxX = Math.max(leftCheek.x, rightCheek.x) * videoWidth;
      faceTopY = foreheadTop.y * videoHeight;
      faceBottomY = chinBottom.y * videoHeight;
    } else if (bounds) {
      faceMinX = bounds.minX * videoWidth;
      faceMaxX = bounds.maxX * videoWidth;
      faceTopY = bounds.minY * videoHeight;
      faceBottomY = bounds.maxY * videoHeight;
    } else {
      return webcamRef.current?.getScreenshot() || null;
    }

    const baseWidth = Math.max(1, faceMaxX - faceMinX);
    const baseHeight = Math.max(1, faceBottomY - faceTopY);
    const estimatedPhotoHeight = baseHeight / HEAD_RATIO_TARGET;
    const minWidthByFace = baseWidth * (1 + (SIDE_MARGIN_FROM_FACE_RATIO * 2));
    let sw = Math.max(estimatedPhotoHeight * PASSPORT_ASPECT_RATIO, minWidthByFace);
    let sh = sw / PASSPORT_ASPECT_RATIO;

    // If width grew to satisfy side margins, preserve head ratio intent by ensuring enough height.
    const minHeightByHeadRatio = baseHeight / HEAD_RATIO_TARGET;
    if (sh < minHeightByHeadRatio) {
      sh = minHeightByHeadRatio;
      sw = sh * PASSPORT_ASPECT_RATIO;
    }

    const topMargin = Math.max(sh * MIN_TOP_MARGIN_RATIO, sh - baseHeight);
    const centerX = (faceMinX + faceMaxX) / 2;
    let sx = centerX - (sw / 2);
    let sy = faceTopY - topMargin;

    sx = clamp(sx, 0, videoWidth - 1);
    sy = clamp(sy, 0, videoHeight - 1);
    sw = clamp(sw, FACE_CROP_MIN_SIZE, videoWidth - sx);
    sh = clamp(sh, FACE_CROP_MIN_SIZE, videoHeight - sy);

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    const ctx = canvas.getContext('2d');
    if (!ctx) return webcamRef.current?.getScreenshot() || null;

    if (isMirroredRef.current) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(
      video,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL('image/jpeg', 0.95);
  }, []);

  const playCaptureSound = useCallback(() => {
    try {
      if (!captureSoundRef.current) {
        const audio = new Audio(CAPTURE_SOUND_SOURCE);
        audio.preload = 'auto';
        audio.volume = 1;
        captureSoundRef.current = audio;
      }

      const audio = captureSoundRef.current;
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error('Capture sound playback blocked:', error);
      });
    } catch (error) {
      console.error('Capture sound playback failed:', error);
    }
  }, []);

  const tryAutoCapture = useCallback(() => {
    if (hasCapturedRef.current) return;
    const image = captureFaceOnlyImage();
    if (!image) return;

    hasCapturedRef.current = true;
    clearTimer(countdownTimerRef);
    clearTimer(captureEffectTimerRef);
    clearTimer(captureCallbackTimerRef);
    setCountdown(0);
    setGuidance('Captured');
    setCaptureFxActive(true);
    playCaptureSound();

    captureCallbackTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      const sourceRect = cameraViewportRef.current?.getBoundingClientRect?.() || null;
      onCapturedRef.current(image, sourceRect);
    }, CAPTURE_CALLBACK_DELAY_MS);

    captureEffectTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setCaptureFxActive(false);
    }, CAPTURE_EFFECT_MS);
  }, [captureFaceOnlyImage, playCaptureSound]);

  const syncCountdownWithValidation = useCallback(() => {
    const validation = latestValidationRef.current;
    if (!validation?.isValid || hasCapturedRef.current) {
      clearTimer(countdownTimerRef);
      setCountdown(0);
      return;
    }

    if (countdownTimerRef.current) return;

    setCountdown(COUNTDOWN_SECONDS);
    countdownTimerRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      const currentValidation = latestValidationRef.current;

      if (!currentValidation?.isValid || hasCapturedRef.current) {
        clearTimer(countdownTimerRef);
        setCountdown(0);
        return;
      }

      setCountdown((previous) => {
        if (previous <= 1) {
          clearTimer(countdownTimerRef);
          tryAutoCapture();
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  }, [tryAutoCapture]);

  useEffect(() => {
    mountedRef.current = true;
    const meshCanvasElement = meshCanvasRef.current;

    let faceMeshAlive = true;

    const setupFaceMesh = async () => {
      try {
        const faceMesh = await getSharedFaceMesh();
        if (!faceMeshAlive || !mountedRef.current) return;

        faceMesh.onResults((results) => {
          if (!mountedRef.current) return;
          const firstFaceLandmarks = results?.multiFaceLandmarks?.[0] || null;
          latestLandmarksRef.current = firstFaceLandmarks;
          drawFaceMesh(firstFaceLandmarks);
          const faceValidation = validateFaceForStep({
            expectedDirection: expectedDirectionRef.current,
            multiFaceLandmarks: results?.multiFaceLandmarks || [],
          });

          const rawBounds = faceValidation.bounds
            ? {
                minX: faceValidation.bounds.minX,
                minY: faceValidation.bounds.minY,
                maxX: faceValidation.bounds.maxX,
                maxY: faceValidation.bounds.maxY,
              }
            : null;

          const now = performance.now();
          if (now - lastLightingAtRef.current >= LIGHTING_INTERVAL_MS) {
            lastLightingAtRef.current = now;
            runLightingAnalysis(rawBounds);
          }

          const lighting =
            latestLightingRef.current ||
            runLightingAnalysis(rawBounds) || {
              isValid: false,
              status: 'checking',
              guidance: 'Checking Lighting',
            };

          const combined = mergeEnrollmentValidation(faceValidation, lighting);
          latestValidationRef.current = combined;
          setFaceBounds(combined.bounds || null);
          updateGuidanceSmoothly(combined.guidance);
          syncCountdownWithValidation();
        });

        faceMeshRef.current = faceMesh;
        setModelReady(true);
      } catch (error) {
        console.error('Face model init failed:', error);
        setModelError('Unable to load face guidance model. Please refresh and try again.');
      }
    };

    setupFaceMesh();

    return () => {
      faceMeshAlive = false;
      mountedRef.current = false;
      latestLandmarksRef.current = null;
      latestValidationRef.current = null;
      clearTimer(countdownTimerRef);
      if (frameRequestRef.current) {
        cancelAnimationFrame(frameRequestRef.current);
        frameRequestRef.current = null;
      }
      clearTimer(captureEffectTimerRef);
      clearTimer(captureCallbackTimerRef);
      if (faceMeshRef.current?.onResults) {
        faceMeshRef.current.onResults(() => {});
      }
      faceMeshRef.current = null;
      if (captureSoundRef.current) {
        captureSoundRef.current.pause();
        captureSoundRef.current.currentTime = 0;
        captureSoundRef.current = null;
      }
      resetEnrollmentVoice();
      if (meshCanvasElement) {
        const ctx = meshCanvasElement.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, meshCanvasElement.width, meshCanvasElement.height);
        }
      }
      isCameraReadyRef.current = false;
      stopStream();
    };
  }, [drawFaceMesh, runLightingAnalysis, syncCountdownWithValidation, updateGuidanceSmoothly]);

  useEffect(() => {
    resetForStep();
  }, [stepKey, expectedDirection, resetForStep]);

  useEffect(() => {
    if (!modelReady || !isCameraReady) return undefined;

    let cancelled = false;

    const processFrame = async () => {
      if (cancelled) return;

      const now = performance.now();
      if (now - lastFrameAtRef.current < DETECTION_INTERVAL_MS) {
        frameRequestRef.current = requestAnimationFrame(processFrame);
        return;
      }

      const video = webcamRef.current?.video;
      const videoHasFrame = Boolean(video && (video.readyState >= 2 || video.videoWidth > 0));
      if (!video || !videoHasFrame || isSendingRef.current) {
        if (video && video.paused) {
          video.play().catch(() => {});
        }
        if (videoHasFrame && now - lastLightingAtRef.current >= LIGHTING_INTERVAL_MS) {
          lastLightingAtRef.current = now;
          runLightingAnalysis(latestValidationRef.current?.bounds || null);
        }
        frameRequestRef.current = requestAnimationFrame(processFrame);
        return;
      }
      if (video.paused) {
        video.play().catch(() => {});
      }
      ensureCanvasSize();

      if (now - lastLightingAtRef.current >= LIGHTING_INTERVAL_MS) {
        lastLightingAtRef.current = now;
        runLightingAnalysis(latestValidationRef.current?.bounds || null);
      }

      lastFrameAtRef.current = now;
      isSendingRef.current = true;

      try {
        await faceMeshRef.current?.send({ image: video });
      } catch (error) {
        if (!cancelled) {
          console.error('Face processing failed:', error);
        }
      } finally {
        isSendingRef.current = false;
      }

      frameRequestRef.current = requestAnimationFrame(processFrame);
    };

    frameRequestRef.current = requestAnimationFrame(processFrame);

    return () => {
      cancelled = true;
      if (frameRequestRef.current) {
        cancelAnimationFrame(frameRequestRef.current);
        frameRequestRef.current = null;
      }
    };
  }, [ensureCanvasSize, modelReady, isCameraReady, runLightingAnalysis]);

  const handleUserMedia = (stream) => {
    const facing = readStreamFacingMode(stream);
    if (facing) {
      setActiveFacingMode(facing);
    } else {
      const selected = videoDevices.find((device) => device.deviceId === selectedDeviceIdRef.current);
      setActiveFacingMode(selected?.facingHint === 'environment' ? 'environment' : 'user');
    }

    isCameraReadyRef.current = true;
    setIsCameraReady(true);
    setCameraError('');
    onPermissionDenied?.('');
    onCameraReady?.();
    ensureCanvasSize();
    void refreshVideoDevices();
  };

  const handleUserMediaError = (error) => {
    console.error('Camera error:', error);
    const permissionDenied =
      error?.name === 'NotAllowedError' ||
      error?.name === 'PermissionDeniedError' ||
      /denied|notallowed/i.test(error?.message || '');

    const overconstrained =
      error?.name === 'OverconstrainedError' ||
      /overconstrained|constraint/i.test(error?.message || '');

    if (overconstrained) {
      selectedDeviceIdRef.current = '';
      setSelectedDeviceId('');
      setActiveFacingMode('user');
      isCameraReadyRef.current = false;
      setIsCameraReady(false);
      setCameraSessionKey((previous) => previous + 1);
      setCameraError('');
      return;
    }

    const message = permissionDenied
      ? 'Camera permission denied. Please allow webcam access and retry.'
      : 'Unable to access webcam. Please check if another app is using the camera.';

    isCameraReadyRef.current = false;
    setCameraError(message);
    setIsCameraReady(false);
    onPermissionDenied(message);
  };

  const loading = !modelReady || !isCameraReady;

  const displayFaceBounds = useMemo(() => {
    if (!faceBounds) return null;
    if (!isMirrored) return faceBounds;
    return {
      ...faceBounds,
      minX: 1 - faceBounds.minX - faceBounds.width,
      centerX: 1 - faceBounds.centerX,
    };
  }, [faceBounds, isMirrored]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {modelError ? <Alert severity="error">{modelError}</Alert> : null}

      <canvas ref={lightingCanvasRef} style={{ display: 'none' }} aria-hidden="true" />

      {showDesktopCameraSelect ? (
        <FormControl size="small" fullWidth sx={{ maxWidth: { sm: 420 } }}>
          <InputLabel id="enrollment-camera-select-label">Camera</InputLabel>
          <Select
            labelId="enrollment-camera-select-label"
            label="Camera"
            value={selectedDeviceId}
            onChange={handleCameraSelectChange}
            displayEmpty
            disabled={loading || videoDevices.length === 0 || !selectedDeviceId}
          >
            {videoDevices.map((device) => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      <Box
        ref={cameraViewportRef}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: '100%', md: 900 },
          mx: 'auto',
          aspectRatio: { xs: '3 / 4', sm: '4 / 3', lg: '16 / 9' },
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#000',
          border: '1px solid #cbd5e1',
        }}
      >
        <Webcam
          key={`webcam-${cameraSessionKey}-${isMobileDevice ? activeFacingMode : selectedDeviceId || 'default'}`}
          ref={webcamRef}
          audio={false}
          mirrored={isMirrored}
          screenshotFormat="image/jpeg"
          screenshotQuality={0.95}
          videoConstraints={videoConstraints}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleUserMediaError}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {showMobileCameraSwitch ? (
          <Tooltip title={activeFacingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}>
            <IconButton
              onClick={handleFlipCamera}
              disabled={loading}
              aria-label={activeFacingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 6,
                bgcolor: 'rgba(15, 23, 42, 0.62)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.82)' },
              }}
            >
              <Cameraswitch />
            </IconButton>
          </Tooltip>
        ) : null}

        <canvas
          ref={meshCanvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        <FaceGuideOverlay
          guidance={guidance}
          countdown={countdown}
          bounds={displayFaceBounds}
          stepPrompt={getDefaultStepPrompt(expectedDirection)}
        />

        {captureFxActive ? (
          <Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              inset: 0,
              zIndex: 8,
              '@keyframes captureFlash': {
                '0%': { opacity: 0.7 },
                '100%': { opacity: 0 },
              },
              animation: 'captureFlash 300ms ease-out forwards',
              bgcolor: 'rgba(255,255,255,0.85)',
            }}
          />
        ) : null}

        {captureFxActive ? (
          <Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              inset: 0,
              zIndex: 9,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: 99,
                bgcolor: 'rgba(15, 23, 42, 0.78)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                border: '1px solid rgba(255,255,255,0.38)',
                '@keyframes capturePop': {
                  '0%': { opacity: 0, transform: 'scale(0.72)' },
                  '48%': { opacity: 1, transform: 'scale(1.06)' },
                  '100%': { opacity: 0, transform: 'scale(0.96)' },
                },
                animation: 'capturePop 420ms ease-out forwards',
              }}
            >
              <CheckCircleRounded fontSize="small" />
              <Typography variant="body2" fontWeight={700}>
                Captured
              </Typography>
            </Box>
          </Box>
        ) : null}

        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(15,23,42,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <CircularProgress color="inherit" />
            <Typography color="#fff" variant="body2">
              Initializing camera and face guide...
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default WebcamCapture;
